import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AssessmentDetail from './AssessmentDetail';
import AssessmentsList from './AssessmentsList';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Card from '@/components/Card';
import { Body, Heading } from '@/components/Typography';
import {
  Assessment,
  AssessmentsAPI,
  PaginationInfo,
} from '@/services/assessmentsAPI';
import useDebouncedValue from '@/hooks/useDebouncedValue';

const AiAssessments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | undefined>();

  // Get params from URL query params
  const queryParams = new URLSearchParams(location.search);
  const externalIdFromUrl = queryParams.get('externalId');
  const reviewedParam = queryParams.get('reviewed');
  const includePendingParam = queryParams.get('include_pending');
  const searchParam = queryParams.get('search');
  const skillNameParam = queryParams.get('skill_name');
  const resultStatusParam = queryParams.get('result_status');
  const pageParam = queryParams.get('page');
  const sortParam = queryParams.get('sort');
  const reviewed = reviewedParam === 'true';
  const includePending = includePendingParam === 'true';
  const search = searchParam || '';
  const skillName = skillNameParam || '';
  const resultStatus = resultStatusParam || undefined;
  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const sortOrder: 'asc' | 'desc' = sortParam === 'created_at' ? 'asc' : 'desc';

  // Local state for immediate input values (for responsive UI)
  const [localSearch, setLocalSearch] = useState(search);
  const [localSkillName, setLocalSkillName] = useState(skillName);

  // Debounce the search values (500ms delay)
  const debouncedSearch = useDebouncedValue(localSearch, 500);
  const debouncedSkillName = useDebouncedValue(localSkillName, 500);

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    setLocalSkillName(skillName);
  }, [skillName]);

  // Update URL params when debounced values change
  useEffect(() => {
    if (debouncedSearch !== search) {
      const newParams = new URLSearchParams(location.search);
      if (debouncedSearch) {
        newParams.set('search', debouncedSearch);
      } else {
        newParams.delete('search');
      }
      newParams.delete('page');
      newParams.delete('externalId');
      navigate(`${location.pathname}?${newParams.toString()}`);
    }
  }, [debouncedSearch, search, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (debouncedSkillName !== skillName) {
      const newParams = new URLSearchParams(location.search);
      if (debouncedSkillName) {
        newParams.set('skill_name', debouncedSkillName);
      } else {
        newParams.delete('skill_name');
      }
      newParams.delete('page');
      newParams.delete('externalId');
      navigate(`${location.pathname}?${newParams.toString()}`);
    }
  }, [
    debouncedSkillName,
    skillName,
    location.search,
    location.pathname,
    navigate,
  ]);

  // Load assessments list and specific assessment if URL has externalId
  useEffect(() => {
    if (externalIdFromUrl) {
      // Load both list and specific assessment in parallel
      loadAssessments();
      loadSpecificAssessment(externalIdFromUrl);
    } else {
      // Just load the list
      loadAssessments();
    }
  }, [
    reviewed,
    includePending,
    search,
    skillName,
    resultStatus,
    page,
    sortParam,
  ]);

  // Handle external ID from URL after assessments list is loaded
  useEffect(() => {
    if (externalIdFromUrl && assessments.length > 0 && !selectedAssessment) {
      const assessment = assessments.find(
        (a) => a.external_id === externalIdFromUrl,
      );
      if (assessment) {
        setSelectedAssessment(assessment);
      }
    }
  }, [externalIdFromUrl, assessments]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const sortValue = sortParam || '-created_at';
      const response = await AssessmentsAPI.getAssessments({
        reviewed: reviewed,
        include_pending: includePending,
        search: search || undefined,
        skill_name: skillName || undefined,
        result_status: resultStatus,
        page: page,
        per_page: 10,
        sort: sortValue as 'created_at' | '-created_at',
      });
      setAssessments(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load assessments');
      console.error('Error loading assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificAssessment = async (externalId: string) => {
    try {
      const response = await AssessmentsAPI.getAssessment(externalId);
      // Create a basic Assessment object from the detailed response
      const basicAssessment: Assessment = {
        external_id: response.data.external_id,
        user_id: response.data.user_id,
        skill_id: response.data.skill_id,
        status: response.data.status,
        language: response.data.language,
        created_at: response.data.created_at,
        reviewed_at: response.data.reviewed_at,
      };
      setSelectedAssessment(basicAssessment);
    } catch (err) {
      console.error('Error loading specific assessment:', err);
      // Don't set error state here, let the list load
    }
  };

  const handleSelectAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);

    // Update URL with external ID and preserve other params
    const newParams = new URLSearchParams(location.search);
    newParams.set('externalId', assessment.external_id);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleReviewedToggle = (newReviewed: boolean) => {
    const newParams = new URLSearchParams(location.search);
    if (newReviewed) {
      newParams.set('reviewed', 'true');
      newParams.delete('include_pending'); // Clear pending filter since reviewed items can't be pending
    } else {
      newParams.delete('reviewed');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleIncludePendingToggle = (newIncludePending: boolean) => {
    const newParams = new URLSearchParams(location.search);
    if (newIncludePending) {
      newParams.set('include_pending', 'true');
    } else {
      newParams.delete('include_pending');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleSearchChange = (newSearch: string) => {
    setLocalSearch(newSearch);
  };

  const handleSkillFilterChange = (newSkillName: string) => {
    setLocalSkillName(newSkillName);
  };

  const handleResultStatusFilterChange = (
    newResultStatus: string | undefined,
  ) => {
    const newParams = new URLSearchParams(location.search);
    if (newResultStatus) {
      newParams.set('result_status', newResultStatus);
    } else {
      newParams.delete('result_status');
    }
    newParams.delete('page'); // Reset to page 1
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('page', newPage.toString());
    newParams.delete('externalId'); // Clear selection when changing pages
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleSortChange = (newSortOrder: 'asc' | 'desc') => {
    const newParams = new URLSearchParams(location.search);
    const sortValue = newSortOrder === 'desc' ? '-created_at' : 'created_at';
    newParams.set('sort', sortValue);
    newParams.delete('page'); // Reset to page 1 when sorting
    newParams.delete('externalId'); // Clear selection
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleReviewSuccess = () => {
    // Reload assessments list after review
    loadAssessments();
    setSelectedAssessment(null);
    navigate(location.pathname);
  };

  return (
    <Page title="AI Assessments">
      <Stack
        gap={24}
        css={{ minHeight: '600px' }}
        style={{ alignItems: 'flex-start' }}
      >
        <div style={{ flex: '0 0 400px' }}>
          <AssessmentsList
            assessments={assessments}
            selectedAssessment={selectedAssessment}
            onSelectAssessment={handleSelectAssessment}
            loading={loading}
            error={error}
            onRefresh={loadAssessments}
            pagination={pagination}
            onPageChange={handlePageChange}
            reviewed={reviewed}
            onReviewedToggle={handleReviewedToggle}
            includePending={includePending}
            onIncludePendingToggle={handleIncludePendingToggle}
            search={localSearch}
            onSearchChange={handleSearchChange}
            skillName={localSkillName}
            onSkillFilterChange={handleSkillFilterChange}
            resultStatus={resultStatus}
            onResultStatusFilterChange={handleResultStatusFilterChange}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Card changeStyle={{ padding: 24 }}>
            {selectedAssessment ? (
              <AssessmentDetail
                assessment={selectedAssessment}
                onReviewSuccess={handleReviewSuccess}
              />
            ) : (
              <Stack
                vertical
                align="center"
                justify="center"
                css={{ minHeight: '400px' }}
              >
                <Heading>No Assessment Selected</Heading>
                <Body>Select an assessment from the list to view details</Body>
              </Stack>
            )}
          </Card>
        </div>
      </Stack>
    </Page>
  );
};

export default AiAssessments;
